import {
    AlertTriangle,
    ArrowUpRight,
    Banknote,
    Bot,
    Briefcase,
    CalendarClock,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    CircleDot,
    FileText,
    HelpCircle,
    LoaderCircle,
    Receipt,
    Search,
    Send,
    Sparkles,
    ThumbsDown,
    ThumbsUp,
    UserCog,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../../References/URLs'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import styles from './KnowledgeBankScreen.module.css'

const PLACEHOLDERS = [
    'How is PF calculated?',
    'What happens when I finalize payroll?',
    'Staff ne resign kiya, ab kya karna hai?',
    'How do I share payslips on WhatsApp?',
]

const SECTION_ICON_MAP = {
    PAYROLL: Briefcase,
    PF_ESI: Receipt,
    SALARY_SLIP: FileText,
    ADVANCE: Banknote,
    FNF: UserCog,
    LEAVE: CalendarClock,
}

const LOADING_STAGES = [
    'Thinking...',
    'Gathering the information...',
    'Matching best results...',
    'Showing results...',
]

const TOPIC_ROTATION_WINDOW_MS = 4 * 60 * 60 * 1000

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const stableStringHash = (value) => {
    const input = String(value || '')
    let hash = 0
    for (let index = 0; index < input.length; index += 1) {
        hash = (hash << 5) - hash + input.charCodeAt(index)
        hash |= 0
    }
    return Math.abs(hash)
}

const getRotationSlot = (timestamp = Date.now()) => Math.floor(timestamp / TOPIC_ROTATION_WINDOW_MS)

const buildQuickTopicSections = ({ sectionList, rotationSlot }) =>
    sectionList
        .map((section, index) => {
            const stableKeyBase =
                section?.sectionIdentifier ||
                section?.sectionCode ||
                section?.sectionTitle ||
                `section-${index}`
            const stableKey = section?.sectionIdentifier || section?.sectionCode || section?.sectionTitle
            return {
                section,
                index,
                score: stableStringHash(`${rotationSlot}-${stableKey}`),
                renderKey: stableKey,
            }
        })
        .sort((left, right) => {
            if (left.score === right.score) return left.index - right.index
            return left.score - right.score
        })
        .slice(0, 6)
        .map((entry) => ({
            section: entry.section,
            renderKey: entry.renderKey,
        }))



const formatBoldSegments = (line) => {
    const parts = String(line || '').split('**')
    return parts.map((part, index) => (index % 2 === 1 ? <strong key={`b-${index}`}>{part}</strong> : part))
}

const renderMarkdownLite = (content) =>
    String(content || '')
        .split('\n')
        .map((line, index) => {
            const trimmed = line.trim()
            if (!trimmed) return null
            if (trimmed.startsWith('- ')) {
                return (
                    <p key={`li-${index}`} className={styles.markdownListItem}>
                        • {formatBoldSegments(trimmed.slice(2))}
                    </p>
                )
            }
            if (trimmed.startsWith('### ')) return <h4 key={`h4-${index}`}>{formatBoldSegments(trimmed.slice(4))}</h4>
            if (trimmed.startsWith('## ')) return <h3 key={`h3-${index}`}>{formatBoldSegments(trimmed.slice(3))}</h3>
            if (trimmed.startsWith('# ')) return <h2 key={`h2-${index}`}>{formatBoldSegments(trimmed.slice(2))}</h2>
            return (
                <p key={`p-${index}`} className={styles.markdownParagraph}>
                    {formatBoldSegments(trimmed)}
                </p>
            )
        })

const KnowledgeBankScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const authToken = authCtx?.token
    const logout = authCtx?.logout
    const storeIdentifier = activeStore?.storeIdentifier

    const [composerText, setComposerText] = useState('')
    const [turns, setTurns] = useState([])
    const [isSearching, setIsSearching] = useState(false)
    const [placeholderIndex, setPlaceholderIndex] = useState(0)
    const [sections, setSections] = useState([])
    const [isSectionsLoading, setIsSectionsLoading] = useState(false)
    const [featuredArticles, setFeaturedArticles] = useState([])
    const [isFeaturedLoading, setIsFeaturedLoading] = useState(false)
    const [feedbackStateMap, setFeedbackStateMap] = useState({})
    const [rotationSlot, setRotationSlot] = useState(() => getRotationSlot())
    const [quickTopicSections, setQuickTopicSections] = useState([])
    const [, setLoadingStageTick] = useState(0)

    const viewStartTimeRef = useRef({})
    const scrollDepthRef = useRef({})
    const viewIdentifierRef = useRef({})
    const latestResponseRef = useRef(null)
    const secondaryRevealTimerRef = useRef({})

    const activePlaceholder = PLACEHOLDERS[placeholderIndex]

    useEffect(() => {
        const sectionList = Array.isArray(sections) ? sections : []
        const nextQuickTopics = buildQuickTopicSections({ sectionList, rotationSlot })

        setQuickTopicSections((currentQuickTopics) => {
            const currentKeys = currentQuickTopics.map((entry) => entry?.renderKey).join('|')
            const nextKeys = nextQuickTopics.map((entry) => entry?.renderKey).join('|')
            return currentKeys === nextKeys ? currentQuickTopics : nextQuickTopics
        })
    }, [rotationSlot, sections])

    useEffect(() => {
        const timer = window.setInterval(() => {
            const nextSlot = getRotationSlot()
            setRotationSlot((currentSlot) => (currentSlot === nextSlot ? currentSlot : nextSlot))
        }, 60 * 1000)

        return () => window.clearInterval(timer)
    }, [])

    useEffect(() => {
        const timer = window.setInterval(() => {
            setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDERS.length)
        }, 4000)
        return () => window.clearInterval(timer)
    }, [])

    useEffect(() => {
        if (!localStorage.getItem('munim_intro_seen')) {
            navigate('/payroll/ask-munim-ji/intro')
        }
    }, [])

    useEffect(() => {
        if (!authToken || !storeIdentifier) return

        let isCancelled = false

        const loadSections = async () => {
            try {
                setIsSectionsLoading(true)
                const response = await fetch(BASE_URL_API + 'payKbGetSections', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authToken,
                        storeIdentifier,
                        audienceType: 'OWNER',
                    }),
                })

                const respo = await response.json()
                if (isCancelled) return
                if (respo?.status === 'success') {
                    setSections(Array.isArray(respo?.response?.sections) ? respo.response.sections : [])
                    return
                }
                if (respo?.message === 'tokenExpired') {
                    logout?.()
                    return
                }
                setSections([])
            } catch {
                if (!isCancelled) setSections([])
            } finally {
                if (!isCancelled) setIsSectionsLoading(false)
            }
        }

        loadSections()

        return () => {
            isCancelled = true
        }
    }, [authToken, storeIdentifier])

    useEffect(() => {
        if (!authToken || !storeIdentifier) return

        let isCancelled = false

        const loadFeaturedArticles = async () => {
            try {
                setIsFeaturedLoading(true)
                const response = await fetch(BASE_URL_API + 'payKbGetFeaturedArticles', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authToken,
                        storeIdentifier,
                        audienceType: 'OWNER',
                        limit: 6,
                    }),
                })

                const respo = await response.json()
                if (isCancelled) return
                if (respo?.status === 'success') {
                    setFeaturedArticles(Array.isArray(respo?.response?.featured) ? respo.response.featured : [])
                    return
                }
                if (respo?.message === 'tokenExpired') {
                    logout?.()
                    return
                }
                setFeaturedArticles([])
            } catch {
                if (!isCancelled) setFeaturedArticles([])
            } finally {
                if (!isCancelled) setIsFeaturedLoading(false)
            }
        }

        loadFeaturedArticles()

        return () => {
            isCancelled = true
        }
    }, [authToken, storeIdentifier])

    const logSearchClick = useCallback(
        (searchIdentifier, articleIdentifier, clickPosition) => {
            if (!searchIdentifier || !articleIdentifier) return
            fetch(BASE_URL_API + 'payKbLogSearchClick', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    searchIdentifier,
                    articleIdentifier,
                    clickPosition,
                }),
            }).catch(() => { })
        },
        [activeStore?.storeIdentifier, authCtx?.token]
    )

    const startArticleView = useCallback((article) => {
        const articleIdentifier = article?.articleIdentifier
        if (!articleIdentifier) return
        viewStartTimeRef.current[articleIdentifier] = Date.now()
        scrollDepthRef.current[articleIdentifier] = 0
        viewIdentifierRef.current[articleIdentifier] = article?.viewIdentifier || null
    }, [])

    const logArticleViewEnd = useCallback(
        (articleIdentifier, fallbackViewIdentifier = null) => {
            if (!articleIdentifier) return
            const viewIdentifier = viewIdentifierRef.current[articleIdentifier] || fallbackViewIdentifier
            if (!viewIdentifier) return

            const startedAt = viewStartTimeRef.current[articleIdentifier]
            const timeSpentSeconds = startedAt ? Math.max(1, Math.round((Date.now() - startedAt) / 1000)) : 1
            const rawDepth = Number(scrollDepthRef.current[articleIdentifier] ?? 0)
            const scrollDepthPercent = Math.min(100, Math.max(0, rawDepth))

            fetch(BASE_URL_API + 'payKbLogArticleView', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    viewIdentifier,
                    timeSpentSeconds,
                    scrollDepthPercent,
                }),
            }).catch(() => { })

            delete viewStartTimeRef.current[articleIdentifier]
            delete scrollDepthRef.current[articleIdentifier]
            delete viewIdentifierRef.current[articleIdentifier]
        },
        [activeStore?.storeIdentifier, authCtx?.token]
    )

    const patchTurn = (turnIdentifier, patchOrUpdater) => {
        setTurns((prev) =>
            prev.map((turn) => {
                if (turn.id !== turnIdentifier) return turn
                if (typeof patchOrUpdater === 'function') {
                    return patchOrUpdater(turn)
                }
                return { ...turn, ...patchOrUpdater }
            })
        )
    }

    const createTurn = (queryText) => ({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        createdAt: Date.now(),
        query: queryText,
        isLoading: true,
        noResults: false,
        suggestion: null,
        isLowConfidence: false,
        searchId: null,
        results: [],
        expandedSecondaryArticleIds: [],
        revealedSecondaryCount: 0,
        errorMessage: '',
    })

    const searchKB = useCallback(
        async (q, preferredArticleIdentifier = null) => {
            const turn = createTurn(q)
            setTurns((prev) => [...prev, turn])
            try {
                setIsSearching(true)

                const response = await fetch(BASE_URL_API + 'payKbSearchArticles', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx?.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                        query: q,
                        audienceType: 'OWNER',
                        sessionSource: 'LOKACI_PRO',
                        submittedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                    }),
                })

                const respo = await response.json()
                if (respo?.status === 'success') {
                    const payload = respo?.response || {}
                    const nextResults = Array.isArray(payload?.results) ? payload.results : []
                    const topArticle = nextResults[0] || null
                    if (topArticle?.articleIdentifier) {
                        startArticleView(topArticle)
                    }
                    patchTurn(turn.id, {
                        isLoading: false,
                        results: nextResults,
                        searchId: payload?.searchIdentifier || null,
                        noResults: Boolean(payload?.noResults),
                        suggestion: payload?.suggestion || null,
                        isLowConfidence: Boolean(payload?.isLowConfidence),
                        expandedSecondaryArticleIds:
                            preferredArticleIdentifier && topArticle?.articleIdentifier !== preferredArticleIdentifier
                                ? [preferredArticleIdentifier]
                                : [],
                        revealedSecondaryCount: 0,
                    })

                    if (preferredArticleIdentifier && nextResults.some((entry) => entry?.articleIdentifier === preferredArticleIdentifier)) {
                        const preferredArticle = nextResults.find((entry) => entry?.articleIdentifier === preferredArticleIdentifier)
                        if (preferredArticle?.articleIdentifier && preferredArticle?.articleIdentifier !== topArticle?.articleIdentifier) {
                            startArticleView(preferredArticle)
                            logSearchClick(payload?.searchIdentifier || null, preferredArticle.articleIdentifier, 1)
                        }
                    }
                    return
                }
                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }
                patchTurn(turn.id, {
                    isLoading: false,
                    errorMessage: respo?.message || 'Unable to fetch help content.',
                })
                toast.error(respo?.message || 'Unable to fetch help content.')
            } catch {
                patchTurn(turn.id, {
                    isLoading: false,
                    errorMessage: 'KB search failed. Please retry.',
                })
                toast.error('KB search failed. Please retry.')
            } finally {
                setIsSearching(false)
            }
        },
        [activeStore?.storeIdentifier, authCtx, logSearchClick, startArticleView]
    )

    const submitComposer = () => {
        const text = composerText.trim()
        if (text.length < 2) {
            toast.info('Please type a slightly longer question.')
            return
        }
        setComposerText('')
        searchKB(text)
    }

    const runTopicSearch = useCallback((topic) => {
        setComposerText('')
        searchKB(topic)
    }, [searchKB])

    const openFeaturedArticle = (article) => {
        const targetQuery = String(article?.question || article?.sectionTitle || '').trim()
        if (!targetQuery) return
        setComposerText('')
        searchKB(targetQuery, article?.articleIdentifier || null)
    }

    const handleSectionPress = (section) => {
        if (!section?.sectionIdentifier) return
        navigate(`/kb/section/${section.sectionIdentifier}`, {
            state: {
                sectionIdentifier: section.sectionIdentifier,
                sectionName: section.sectionName,
                sectionCode: section.sectionCode,
            },
        })
    }

    const onToggleSecondaryArticle = (turn, article, position) => {
        try {
            const expandedIds = Array.isArray(turn?.expandedSecondaryArticleIds) ? turn.expandedSecondaryArticleIds : []
            const isAlreadyExpanded = expandedIds.includes(article.articleIdentifier)

            if (isAlreadyExpanded) {
                logArticleViewEnd(article.articleIdentifier, article?.viewIdentifier || null)
                patchTurn(turn.id, {
                    expandedSecondaryArticleIds: expandedIds.filter((entry) => entry !== article.articleIdentifier),
                })
                return
            }

            startArticleView(article)
            logSearchClick(turn?.searchId, article.articleIdentifier, position)
            patchTurn(turn.id, {
                expandedSecondaryArticleIds: [...expandedIds, article.articleIdentifier],
            })
        } catch {
            patchTurn(turn.id, {
                expandedSecondaryArticleIds: [article.articleIdentifier],
            })
        }
    }

    useEffect(() => {
        return () => {
            Object.keys(viewIdentifierRef.current).forEach((articleIdentifier) => {
                logArticleViewEnd(articleIdentifier, viewIdentifierRef.current[articleIdentifier])
            })
        }
    }, [logArticleViewEnd])

    useEffect(() => {
        if (!turns.some((turn) => turn?.isLoading)) return undefined
        const timer = window.setInterval(() => {
            setLoadingStageTick((prev) => prev + 1)
        }, 350)
        return () => window.clearInterval(timer)
    }, [turns])

    useEffect(() => {
        turns.forEach((turn) => {
            const secondaryCount = Math.max((turn?.results || []).length - 1, 0)
            const visibleSecondaryCount = Math.min(
                Number(turn?.revealedSecondaryCount ?? secondaryCount),
                secondaryCount
            )
            const hasTimer = Boolean(secondaryRevealTimerRef.current[turn.id])

            if (turn?.isLoading || secondaryCount === 0 || visibleSecondaryCount >= secondaryCount) {
                if (hasTimer) {
                    window.clearTimeout(secondaryRevealTimerRef.current[turn.id])
                    delete secondaryRevealTimerRef.current[turn.id]
                }
                return
            }

            if (hasTimer) return

            secondaryRevealTimerRef.current[turn.id] = window.setTimeout(() => {
                delete secondaryRevealTimerRef.current[turn.id]
                patchTurn(turn.id, (currentTurn) => {
                    const currentSecondaryCount = Math.max((currentTurn?.results || []).length - 1, 0)
                    const currentVisibleCount = Math.min(
                        Number(currentTurn?.revealedSecondaryCount ?? currentSecondaryCount),
                        currentSecondaryCount
                    )
                    return {
                        ...currentTurn,
                        revealedSecondaryCount: Math.min(currentSecondaryCount, currentVisibleCount + 1),
                    }
                })
            }, 160)
        })
    }, [turns])

    useEffect(() => {
        return () => {
            Object.values(secondaryRevealTimerRef.current).forEach((timerId) => window.clearTimeout(timerId))
            secondaryRevealTimerRef.current = {}
        }
    }, [])

    const latestTurnIsLoading = turns.length > 0 ? Boolean(turns[turns.length - 1]?.isLoading) : false

    useEffect(() => {
        if (!latestResponseRef.current) return
        latestResponseRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [turns.length, latestTurnIsLoading])

    const getLoadingStageMessage = (turn) => {
        const startedAt = Number(turn?.createdAt || Date.now())
        const elapsedMs = Math.max(0, Date.now() - startedAt)
        const stageIndex = Math.min(LOADING_STAGES.length - 1, Math.floor(elapsedMs / 800))
        return LOADING_STAGES[stageIndex]
    }

    const getFeedbackState = (articleIdentifier) => {
        const base = {
            voted: false,
            isHelpful: null,
            noteVisible: false,
            noteText: '',
            isSubmitting: false,
            submitted: false,
            message: '',
        }
        if (!articleIdentifier) return base
        return feedbackStateMap[articleIdentifier] || base
    }

    const patchFeedbackState = (articleIdentifier, patch) => {
        if (!articleIdentifier) return
        setFeedbackStateMap((prev) => ({
            ...prev,
            [articleIdentifier]: {
                voted: false,
                isHelpful: null,
                noteVisible: false,
                noteText: '',
                isSubmitting: false,
                submitted: false,
                message: '',
                ...(prev[articleIdentifier] || {}),
                ...patch,
            },
        }))
    }

    const submitFeedback = async ({ article, isHelpful, feedbackNote = '' }) => {
        if (!article?.articleIdentifier || !article?.sectionIdentifier) return

        patchFeedbackState(article.articleIdentifier, {
            isSubmitting: true,
            voted: true,
            isHelpful,
        })

        try {
            const response = await fetch(BASE_URL_API + 'payKbSubmitFeedback', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    articleIdentifier: article.articleIdentifier,
                    sectionIdentifier: article.sectionIdentifier,
                    isHelpful,
                    feedbackNote,
                    submittedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                    audienceType: 'OWNER',
                    sessionSource: 'LOKACI_PRO',
                }),
            })

            const respo = await response.json()
            if (respo?.status === 'success') {
                const successMessage = respo?.message || (isHelpful ? 'Glad this helped.' : 'Thanks for the feedback.')
                patchFeedbackState(article.articleIdentifier, {
                    isSubmitting: false,
                    submitted: true,
                    noteVisible: false,
                    message: successMessage,
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            patchFeedbackState(article.articleIdentifier, {
                isSubmitting: false,
                submitted: false,
                noteVisible: !isHelpful,
            })
            toast.error(respo?.message || 'Could not submit feedback.')
        } catch {
            patchFeedbackState(article.articleIdentifier, {
                isSubmitting: false,
                submitted: false,
                noteVisible: !isHelpful,
            })
            toast.error('Feedback submission failed. Please retry.')
        }
    }

    const showingFeatured = turns.length === 0

    const renderExpandedArticleBody = (article, relevance) => {
        const feedbackState = getFeedbackState(article?.articleIdentifier)
        return (
            <div
                className={styles.fullAnswerContainer}
                onScroll={(event) => {
                    const element = event.currentTarget
                    const totalHeight = Math.max(element.scrollHeight, 1)
                    const viewedHeight = element.scrollTop + element.clientHeight
                    const nextDepth = Math.round((viewedHeight / totalHeight) * 100)
                    const prevDepth = Number(scrollDepthRef.current[article.articleIdentifier] || 0)
                    scrollDepthRef.current[article.articleIdentifier] = Math.min(100, Math.max(prevDepth, nextDepth))
                }}
            >
                {renderMarkdownLite(article.answer)}
                <div className={styles.articleMetaRow}>
                    <span><CircleDot size={12} /> Helpful: {Number(article.helpfulScore || 0).toFixed(1)}</span>
                    <span><CircleDot size={12} /> Relevance: {(Number(relevance || 0) * 100).toFixed(1)}%</span>
                </div>

                {!feedbackState.submitted ? (
                    <div className={styles.feedbackRow}>
                        {!feedbackState.noteVisible ? (
                            <>
                                <span className={styles.feedbackLabel}>Was this helpful?</span>
                                <button
                                    type="button"
                                    className={`${styles.feedbackVoteBtn} ${styles.feedbackYesBtn}`}
                                    disabled={feedbackState.isSubmitting}
                                    onClick={() => submitFeedback({ article, isHelpful: true, feedbackNote: '' })}
                                >
                                    <ThumbsUp size={12} /> Yes
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.feedbackVoteBtn} ${styles.feedbackNoBtn}`}
                                    disabled={feedbackState.isSubmitting}
                                    onClick={() =>
                                        patchFeedbackState(article.articleIdentifier, {
                                            voted: true,
                                            isHelpful: false,
                                            noteVisible: true,
                                        })
                                    }
                                >
                                    <ThumbsDown size={12} /> No
                                </button>
                            </>
                        ) : (
                            <div className={styles.feedbackNoteContainer}>
                                <label className={styles.feedbackNoteLabel} htmlFor={`feedback-note-${article.articleIdentifier}`}>
                                    What was missing?
                                </label>
                                <textarea
                                    id={`feedback-note-${article.articleIdentifier}`}
                                    className={styles.feedbackNoteInput}
                                    placeholder="Tell us what could be better..."
                                    value={feedbackState.noteText}
                                    rows={3}
                                    onChange={(event) =>
                                        patchFeedbackState(article.articleIdentifier, {
                                            noteText: event.target.value,
                                        })
                                    }
                                />
                                <div className={styles.feedbackNoteActions}>
                                    <button
                                        type="button"
                                        className={styles.feedbackCancelBtn}
                                        disabled={feedbackState.isSubmitting}
                                        onClick={() =>
                                            patchFeedbackState(article.articleIdentifier, {
                                                noteVisible: false,
                                                noteText: '',
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.feedbackSubmitBtn}
                                        disabled={feedbackState.isSubmitting}
                                        onClick={() =>
                                            submitFeedback({
                                                article,
                                                isHelpful: false,
                                                feedbackNote: feedbackState.noteText,
                                            })
                                        }
                                    >
                                        {feedbackState.isSubmitting ? <LoaderCircle size={12} className={styles.spinIcon} /> : <Send size={12} />} Submit
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className={styles.feedbackThankYouText}>
                        {feedbackState.isHelpful ? '✓ Glad this helped!' : '✓ Thanks for the feedback.'}
                    </p>
                )}

                {article?.ctaRoute && article?.ctaLabel ? (
                    <button
                        type="button"
                        className={styles.ctaButton}
                        onClick={() => navigate(article.ctaRoute)}
                    >
                        {article.ctaLabel}
                    </button>
                ) : null}
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <header className={styles.topBar}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={16} />
                </button>
                <div className={styles.titleWrap}>
                    <span className={styles.topPill}>
                        <Sparkles size={13} /> Help Center
                    </span>
                    <h1>Ask anything about Payroll from MUNIM Ji.</h1>
                </div>
                <button
                    type="button"
                    className={styles.munimIntroBtn}
                    onClick={() => navigate('/payroll/ask-munim-ji/intro')}
                    title="About MUNIM ji"
                >
                    <span className={styles.munimIntroBtnRing}>M</span>
                </button>
            </header>

            {showingFeatured ? (
                <section className={styles.topicsWrap}>
                    <div className={styles.sectionHead}><Sparkles size={15} /> <strong>Featured Chats</strong></div>
                    {isFeaturedLoading ? <p className={styles.sectionEmpty}>Loading featured conversations...</p> : null}
                    {!isFeaturedLoading && featuredArticles.length > 0 ? (
                        <div className={styles.featuredScroller}>
                            {featuredArticles.map((article) => (
                                <button
                                    key={article?.articleIdentifier}
                                    type="button"
                                    className={styles.featuredCard}
                                    onClick={() => openFeaturedArticle(article)}
                                >
                                    <span className={styles.featuredSection}>{article?.sectionTitle || 'Knowledge Bank'}</span>
                                    <h4 className={styles.featuredQuestion}>{article?.question || 'Untitled article'}</h4>
                                    <p className={styles.featuredMeta}>
                                        👍 {Number(article?.helpfulScore || 0).toFixed(0)}%
                                        {/* · {Number(article?.viewCount || 0)} views */}
                                    </p>
                                    <span className={styles.featuredOpenHint}>Ask Munim Ji <ArrowUpRight size={12} /></span>
                                </button>
                            ))}
                        </div>
                    ) : null}

                    <div className={styles.sectionHead}><Bot size={15} /> <strong>Browse by Topic</strong></div>
                    {isSectionsLoading ? <p className={styles.sectionEmpty}>Loading topics...</p> : null}
                    {!isSectionsLoading && sections.length === 0 ? <p className={styles.sectionEmpty}>No topics available right now.</p> : null}
                    {!isSectionsLoading && sections.length > 0 ? (
                        <div className={styles.topicGrid}>
                            {quickTopicSections.map((entry) => {
                                const section = entry?.section
                                return (
                                    <button
                                        key={`quick-${entry?.renderKey || section?.sectionIdentifier || section?.sectionCode || section?.sectionTitle}`}
                                        type="button"
                                        className={styles.topicChip}
                                        onClick={() => runTopicSearch(section?.sectionTitle || '')}
                                    >
                                        {section?.sectionTitle || 'Topic'}
                                    </button>
                                )
                            })}
                        </div>
                    ) : null}
                </section>
            ) : (
                <section className={styles.resultsWrap}>
                    <div className={styles.conversationThread}>
                        {turns.map((turn, turnIndex) => {
                            const isLatestTurn = turnIndex === turns.length - 1
                            const topArticle = turn?.results?.[0]
                            const secondaryArticles = Array.isArray(turn?.results) ? turn.results.slice(1) : []
                            const visibleSecondaryCount = Math.min(
                                Number(turn?.revealedSecondaryCount ?? secondaryArticles.length),
                                secondaryArticles.length
                            )
                            const visibleSecondaryArticles = secondaryArticles.slice(0, visibleSecondaryCount)

                            return (
                                <div key={turn.id} className={styles.turnBlock}>
                                    <div className={styles.userBubble}>{turn.query}</div>

                                    <div
                                        className={styles.assistantBubble}
                                        ref={isLatestTurn ? latestResponseRef : null}
                                    >
                                        <div className={styles.sectionHead}>
                                            <Bot size={15} />
                                            <strong>{turn.isLoading ? 'Understanding...' : `${turn.results.length} answer(s) found`}</strong>
                                            {turn.isLowConfidence && !turn.isLoading ? <span className={styles.lowConfidenceTag}>Low confidence</span> : null}
                                        </div>

                                        {turn.isLoading ? <div className={styles.searchingRow}><span className={styles.pulseDot} /><span className={styles.pulseDot} /><span className={styles.pulseDot} /><span className={styles.searchingText}>{getLoadingStageMessage(turn)}</span></div> : null}

                                        {!turn.isLoading && turn.errorMessage ? (
                                            <div className={styles.noResultsBox}>
                                                <AlertTriangle size={16} />
                                                <div>
                                                    <strong>Search failed</strong>
                                                    <p>{turn.errorMessage}</p>
                                                </div>
                                            </div>
                                        ) : null}

                                        {!turn.isLoading && (turn.noResults || (turn.results || []).length === 0) && !turn.errorMessage ? (
                                            <div className={styles.noResultsBox}>
                                                <AlertTriangle size={16} />
                                                <div>
                                                    <strong>No exact match found</strong>
                                                    <p>We could not find a confident answer for this query.</p>
                                                    {turn.suggestion ? (
                                                        <div className={styles.suggestionChips}>
                                                            {turn.suggestion
                                                                .split(',')
                                                                .map((item) => item.trim())
                                                                .filter(Boolean)
                                                                .map((item) => (
                                                                    <button key={`${turn.id}-${item}`} type="button" className={styles.suggestionChip} onClick={() => runTopicSearch(item)}>
                                                                        {item}
                                                                    </button>
                                                                ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}

                                        {!turn.isLoading && topArticle ? (
                                            <article className={`${styles.articleCard} ${styles.topAnswerCard}`}>
                                                <div className={styles.cardHeaderBtn}>
                                                    <div className={styles.questionWrap}>
                                                        <h3>{topArticle.question}</h3>
                                                    </div>
                                                </div>
                                                <p className={styles.answerSummary}>{topArticle.answerSummary}</p>
                                                {renderExpandedArticleBody(topArticle, Number(topArticle?.relevanceScore || 0))}
                                            </article>
                                        ) : null}

                                        {!turn.isLoading && visibleSecondaryArticles.length > 0 ? (
                                            <div className={styles.secondaryCardsWrap}>
                                                {visibleSecondaryArticles.map((article, index) => {
                                                    const expandedIds = Array.isArray(turn?.expandedSecondaryArticleIds)
                                                        ? turn.expandedSecondaryArticleIds
                                                        : []
                                                    const isExpanded = expandedIds.includes(article.articleIdentifier)
                                                    const relevance = Number(article?.relevanceScore || 0)
                                                    return (
                                                        <article
                                                            key={`${turn.id}-${article.articleIdentifier || index}`}
                                                            className={`${styles.secondaryArticleCard} ${isExpanded ? styles.secondaryArticleCardExpanded : ''}`}
                                                        >
                                                            <button
                                                                type="button"
                                                                className={styles.secondaryCardBtn}
                                                                onClick={() => onToggleSecondaryArticle(turn, article, index + 2)}
                                                            >
                                                                <span>{article.question}</span>
                                                                <span className={styles.expandIcon}>{isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                                                            </button>
                                                            <p className={styles.secondarySummary}>{article.answerSummary}</p>
                                                            {isExpanded ? renderExpandedArticleBody(article, relevance) : null}
                                                        </article>
                                                    )
                                                })}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </section>
            )}

            <section className={`${styles.searchShell} ${styles.searchShellBottom}`}>
                <div className={styles.conversationComposerWrap}>
                    <div className={styles.searchBar}>
                        <Search size={18} className={styles.searchIcon} />
                        <input
                            type="text"
                            value={composerText}
                            onChange={(event) => setComposerText(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault()
                                    submitComposer()
                                }
                            }}
                            placeholder={activePlaceholder}
                            className={styles.searchInput}
                        />
                        <button type="button" className={styles.sendBtn} onClick={submitComposer} disabled={isSearching}>
                            {isSearching ? <LoaderCircle size={14} className={styles.spinIcon} /> : <Send size={14} />}
                        </button>
                    </div>
                    <p className={styles.searchHint}>MUNIM Ji is an AI, Type your full question and press Enter to send.</p>
                </div>
            </section>
        </div>
    )
}

export default KnowledgeBankScreen